import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button } from "@wac-ui-dashboard/wac_component_library";
import { updateConfig as globalUpdateConfig } from "../../../../store/slices/Global";
import { updateConfig } from "../../../../store/slices/User/SubscriptionPlans/corporateOfferSlice";
import {
  useActivateCorporateOfferMutation,
  useDeactivateCorporateOfferMutation,
  useGetCorporateOfferListDataQuery,
  useUpdateCorporateOfferTableHeadDataMutation,
} from "../../../../store/queries/Subscription";

const useCorporateOffers = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];
  const dispatch = useDispatch();
  const offerState = useSelector((state) => state.corporateOffer);
  const { showEditModal } = useSelector((state) => state.global);

  const {
    data: offerList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetCorporateOfferListDataQuery({
    page_size: offerState?.currentPageSize,
    page: offerState?.currentPage,
    sort: offerState?.sortBy,
    sort_order: offerState?.sortOrder,
    search: offerState?.search,
  });

  const [updateCorporateOfferFields] =
    useUpdateCorporateOfferTableHeadDataMutation();
  const [activateOffer] = useActivateCorporateOfferMutation();
  const [deactivateOffer] = useDeactivateCorporateOfferMutation();

  const offers = useMemo(() => offerList?.result ?? [], [offerList]);

  useEffect(() => {
    if (offerState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        })
      );
    }
    // eslint-disable-next-line
  }, [offerState.clearSelection]);

  const handleCreateClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = !state.showCreateModal;
        state.isEdit = false;
        state.editOfferData = {};
        state.pricing = {};
      })
    );
  };

  /**
   * The table hands over the checked row's id, the same shape the plan list's edit gets.
   * The row itself is kept as the form's first fill; the fields the row does not carry -
   * the plan id, the eligibility rules - arrive when the form fetches the details.
   */
  const handleEditAction = (e) => {
    const offer = offers?.find((item) => item?.id === e?.[0]);

    dispatch(
      updateConfig((state) => {
        state.showCreateModal = true;
        state.isEdit = true;
        state.selectedId = offer?.offer_id ?? e?.[0];
        state.editOfferData = offer ?? {};
        state.pricing = {};
      })
    );
  };

  const handleDetailsClick = (offer) => {
    dispatch(
      updateConfig((state) => {
        state.showDetailsModal = !state.showDetailsModal;
        state.selectedId = offer?.offer_id ?? "";
      })
    );
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };

  const handleClearClick = () => {
    dispatch(
      updateConfig((state) => {
        state.search = "";
        state.currentPage = 1;
      })
    );
  };

  const handleSort = (label) => {
    if (offerState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder = offerState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };

  /**
   * Activating puts a draft on sale; deactivating stops *new* redemptions only - plans
   * already activated by employees run to their own expiry, which is why this does not
   * read as a delete.
   */
  const handleToggleActive = async (offer) => {
    const isActive = offer?.status_label === "active";
    const action = isActive ? deactivateOffer : activateOffer;
    const response = await action({ offer_id: offer?.offer_id });

    if (response?.data?.status_code === 200) {
      toast.success(isActive ? `Offer deactivated` : `Offer activated`);
      refetch();
    } else {
      toast.error(
        response?.error?.data?.message ??
          response?.data?.message ??
          `Unable to change the offer status`
      );
    }
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      seats: (field, data) => (
        <p className="pro-mb-0">
          {data?.used_seats} / {data?.seats}
        </p>
      ),
      action: (field, data) => (
        <div className="pro-d-flex pro-gap-2 no-wrap">
          <Button
            className={"pro-btn-link sm"}
            onClick={() => handleDetailsClick(data)}
          >
            View
          </Button>
          {/*
            fully_redeemed and expired are worked out at read time and cannot be switched -
            there is nothing to activate when every seat is gone or the window has closed.
          */}
          {offerList?.edit_permission &&
            ["draft", "active", "deactivated"].includes(data?.status_label) && (
              <Button
                className={"pro-btn-link sm"}
                onClick={() => handleToggleActive(data)}
              >
                {data?.status_label === "active" ? "Deactivate" : "Activate"}
              </Button>
            )}
        </div>
      ),
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  return {
    isLoading,
    isFetching,
    offerList,
    offerState,
    showEditModal,
    paginationOptions,
    updateCorporateOfferFields,
    handleEditColumnsClick,
    handleToggleActive,
    handleDetailsClick,
    handleCreateClick,
    handlePagination,
    handleClearClick,
    handleEditAction,
    handlePageSize,
    handleSearch,
    handleSort,
    refetch,
    getRow,
  };
};

export default useCorporateOffers;
